import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../../Firebase';
import './CampaignsPage.css';

const DEFAULT_FILTERS = {
    minSpeed: 0,
    maxPrice: 2000,
    sortBy: 'price-asc'
};

const CampaignsPage = () => {
    const [providers, setProviders] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

    const calculateAveragePrice = (priceTiers, totalDuration) => {
        if (!Array.isArray(priceTiers) || !totalDuration) return 0;

        const totalCost = priceTiers.reduce((sum, tier) => {
            const monthlyPrice = Number(tier?.monthlyPrice || 0);
            const duration = Number(tier?.duration || 0);
            return sum + monthlyPrice * duration;
        }, 0);

        return Math.round(totalCost / totalDuration);
    };

    const normalizeCampaign = (campaign) => {
        const normalizedFeatures = Array.isArray(campaign.features) ? campaign.features : [];
        const normalizedPriceTiers = Array.isArray(campaign.priceTiers) ? campaign.priceTiers : [];

        if (!campaign.pricingModel) {
            return {
                ...campaign,
                features: normalizedFeatures,
                pricingModel: 'fixed',
                fixedMonthlyPrice: Number(campaign.newPrice || 0),
                totalDuration: campaign.totalDuration || 24,
                priceTiers: [
                    {
                        duration: campaign.totalDuration || 24,
                        monthlyPrice: Number(campaign.newPrice || 0),
                        description: 'Taahhüt boyunca'
                    }
                ]
            };
        }

        return {
            ...campaign,
            features: normalizedFeatures,
            priceTiers: normalizedPriceTiers,
            fixedMonthlyPrice: Number(campaign.fixedMonthlyPrice || campaign.newPrice || 0),
            totalDuration: Number(campaign.totalDuration || 0)
        };
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const providersQuery = query(
                    collection(db, 'providers'),
                    where('isActive', '==', true)
                );

                const providersSnapshot = await getDocs(providersQuery);
                const providersData = providersSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setProviders(providersData);

                const allCampaigns = [];

                for (const provider of providersData) {
                    const campaignsQuery = query(
                        collection(db, `providers/${provider.id}/campaigns`),
                        where('isActive', '==', true)
                    );

                    const campaignsSnapshot = await getDocs(campaignsQuery);

                    campaignsSnapshot.forEach((doc) => {
                        const campaignData = normalizeCampaign({
                            id: doc.id,
                            providerId: provider.id,
                            providerName: provider.name,
                            providerLogo: provider.logo,
                            ...doc.data()
                        });

                        allCampaigns.push(campaignData);
                    });
                }

                setCampaigns(allCampaigns);
            } catch (error) {
                console.error('Kampanyalar alınırken hata oluştu:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: name === 'sortBy' ? value : Number(value)
        }));
    };

    const resetFilters = () => {
        setSelectedProvider(null);
        setFilters(DEFAULT_FILTERS);
    };

    const filteredCampaigns = campaigns
        .filter((campaign) => {
            if (selectedProvider && campaign.providerId !== selectedProvider.id) {
                return false;
            }

            if (Number(campaign.speed || 0) < filters.minSpeed) {
                return false;
            }

            const effectivePrice =
                campaign.pricingModel === 'fixed'
                    ? Number(campaign.fixedMonthlyPrice || 0)
                    : calculateAveragePrice(campaign.priceTiers, campaign.totalDuration);

            if (effectivePrice > filters.maxPrice) {
                return false;
            }

            return true;
        })
        .sort((a, b) => {
            const getPrice = (camp) =>
                camp.pricingModel === 'fixed'
                    ? Number(camp.fixedMonthlyPrice || 0)
                    : calculateAveragePrice(camp.priceTiers, camp.totalDuration);

            const priceA = getPrice(a);
            const priceB = getPrice(b);
            const speedA = Number(a.speed || 0);
            const speedB = Number(b.speed || 0);

            switch (filters.sortBy) {
                case 'price-asc':
                    return priceA - priceB;
                case 'price-desc':
                    return priceB - priceA;
                case 'speed-asc':
                    return speedA - speedB;
                case 'speed-desc':
                    return speedB - speedA;
                default:
                    return 0;
            }
        });

    const renderPriceInfo = (campaign) => {
        if (campaign.pricingModel === 'fixed') {
            return (
                <div className="price-section-campaign">
                    <div className="price-display-campaign">
                        {campaign.oldPrice && (
                            <span className="old-price">{campaign.oldPrice} TL</span>
                        )}
                        <span>{campaign.fixedMonthlyPrice} TL</span>
                    </div>
                    <span className="price-description">Taahhüt boyunca sabit fiyat</span>
                </div>
            );
        }

        return (
            <div className="price-section-campaign">
                <div className="price-tiers-campaign">
                    {campaign.priceTiers.slice(0, 2).map((tier, index) => (
                        <div key={index} className="price-tier-campaign">
                            <span className="tier-price-campaign">{tier.monthlyPrice} TL</span>
                            <span className="tier-duration">{tier.description}</span>
                        </div>
                    ))}

                    {campaign.priceTiers.length > 2 && (
                        <div className="price-tier-more-campaign">
                            +{campaign.priceTiers.length - 2} dönem
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="campaigns-page">
            <section className="campaigns-hero">
                <div className="hero-overlay"></div>
                <div className="container">
                    <div className="hero-content">
                        <h1>İnternet Kampanyaları</h1>
                        <p>Size en uygun internet paketini keşfedin</p>
                    </div>
                </div>
            </section>

            <div className="container main-container">
                <aside className="filters-sidebar">
                    <div className="sidebar-header">
                        <h3>Filtrele</h3>
                        <button className="clear-filters" onClick={resetFilters}>
                            Filtreleri Temizle
                        </button>
                    </div>

                    <div className="filter-group">
                        <h4 className="filter-title">İnternet Sağlayıcısı</h4>
                        <div className="provider-filter-grid">
                            {providers.map((provider) => (
                                <div
                                    key={provider.id}
                                    className={`provider-filter-card ${selectedProvider?.id === provider.id ? 'active' : ''
                                        }`}
                                    onClick={() =>
                                        setSelectedProvider(
                                            selectedProvider?.id === provider.id ? null : provider
                                        )
                                    }
                                >
                                    <img
                                        src={provider.logo}
                                        alt={provider.name}
                                        className="provider-logo-campaign"
                                        loading="lazy"
                                    />
                                    <span>{provider.name}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="filter-group">
                        <h4 className="filter-title">Minimum Hız</h4>
                        <div className="range-filter">
                            <input
                                type="range"
                                name="minSpeed"
                                min="8"
                                max="1000"
                                step="8"
                                value={filters.minSpeed}
                                onChange={handleFilterChange}
                                className="range-slider"
                            />
                            <div className="range-values">
                                <span>8 Mbps</span>
                                <span className="current-value">{filters.minSpeed} Mbps</span>
                                <span>1000 Mbps</span>
                            </div>
                        </div>
                    </div>

                    <div className="filter-group">
                        <h4 className="filter-title">Maksimum Fiyat</h4>
                        <div className="range-filter">
                            <input
                                type="range"
                                name="maxPrice"
                                min="50"
                                max="1000"
                                step="50"
                                value={filters.maxPrice}
                                onChange={handleFilterChange}
                                className="range-slider"
                            />
                            <div className="range-values">
                                <span>50 TL</span>
                                <span className="current-value">{filters.maxPrice} TL</span>
                                <span>1000 TL</span>
                            </div>
                        </div>
                    </div>

                    <div className="filter-group">
                        <h4 className="filter-title">Sırala</h4>
                        <select
                            name="sortBy"
                            value={filters.sortBy}
                            onChange={handleFilterChange}
                            className="sort-select"
                        >
                            <option value="price-asc">Fiyat (Artan)</option>
                            <option value="price-desc">Fiyat (Azalan)</option>
                            <option value="speed-asc">Hız (Artan)</option>
                            <option value="speed-desc">Hız (Azalan)</option>
                        </select>
                    </div>
                </aside>

                <button
                    className="mobile-filters-button"
                    onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
                >
                    <svg
                        width="18"
                        height="12"
                        viewBox="0 0 18 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M0 12H18V10H0V12ZM0 7H18V5H0V7ZM0 0V2H18V0H0Z"
                            fill="currentColor"
                        />
                    </svg>
                    Filtrele
                </button>

                {mobileFiltersOpen && (
                    <div className="mobile-filters-panel">
                        <div className="panel-header">
                            <h3>Filtrele</h3>
                            <button
                                className="close-panel"
                                onClick={() => setMobileFiltersOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        <div className="panel-content">
                            <div className="filter-group">
                                <h4 className="filter-title">İnternet Sağlayıcısı</h4>
                                <div className="provider-filter-grid">
                                    {providers.map((provider) => (
                                        <div
                                            key={provider.id}
                                            className={`provider-filter-card ${selectedProvider?.id === provider.id ? 'active' : ''
                                                }`}
                                            onClick={() =>
                                                setSelectedProvider(
                                                    selectedProvider?.id === provider.id
                                                        ? null
                                                        : provider
                                                )
                                            }
                                        >
                                            <img
                                                src={provider.logo}
                                                alt={provider.name}
                                                className="provider-logo-campaign"
                                                loading="lazy"
                                            />
                                            <span>{provider.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="filter-group">
                                <h4 className="filter-title">Minimum Hız</h4>
                                <div className="range-filter">
                                    <input
                                        type="range"
                                        name="minSpeed"
                                        min="8"
                                        max="1000"
                                        step="8"
                                        value={filters.minSpeed}
                                        onChange={handleFilterChange}
                                        className="range-slider"
                                    />
                                    <div className="range-values">
                                        <span>8 Mbps</span>
                                        <span className="current-value">
                                            {filters.minSpeed} Mbps
                                        </span>
                                        <span>1000 Mbps</span>
                                    </div>
                                </div>
                            </div>

                            <div className="filter-group">
                                <h4 className="filter-title">Maksimum Fiyat</h4>
                                <div className="range-filter">
                                    <input
                                        type="range"
                                        name="maxPrice"
                                        min="50"
                                        max="1000"
                                        step="50"
                                        value={filters.maxPrice}
                                        onChange={handleFilterChange}
                                        className="range-slider"
                                    />
                                    <div className="range-values">
                                        <span>50 TL</span>
                                        <span className="current-value">
                                            {filters.maxPrice} TL
                                        </span>
                                        <span>1000 TL</span>
                                    </div>
                                </div>
                            </div>

                            <div className="filter-group">
                                <h4 className="filter-title">Sırala</h4>
                                <select
                                    name="sortBy"
                                    value={filters.sortBy}
                                    onChange={handleFilterChange}
                                    className="sort-select"
                                >
                                    <option value="price-asc">Fiyat (Artan)</option>
                                    <option value="price-desc">Fiyat (Azalan)</option>
                                    <option value="speed-asc">Hız (Artan)</option>
                                    <option value="speed-desc">Hız (Azalan)</option>
                                </select>
                            </div>
                        </div>

                        <div className="panel-footer">
                            <button
                                className="apply-filters"
                                onClick={() => setMobileFiltersOpen(false)}
                            >
                                Filtreleri Uygula
                            </button>
                        </div>
                    </div>
                )}

                <main className="campaigns-main">
                    <div className="results-header">
                        <h2>
                            {selectedProvider
                                ? `${selectedProvider.name} Kampanyaları`
                                : 'Tüm Kampanyalar'}
                            <span className="results-count">
                                {filteredCampaigns.length} sonuç
                            </span>
                        </h2>
                    </div>

                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Kampanyalar yükleniyor...</p>
                        </div>
                    ) : filteredCampaigns.length > 0 ? (
                        <div className="campaigns-grid">
                            {filteredCampaigns.map((campaign) => (
                                <Link
                                    to={`/kampanyalar/${campaign.providerId}/${campaign.id}`}
                                    key={`${campaign.providerId}-${campaign.id}`}
                                    className="campaign-card"
                                >
                                    <div className="card-header">
                                        <img
                                            src={campaign.providerLogo}
                                            alt={campaign.providerName}
                                            className="provider-logo-campaign"
                                            loading="lazy"
                                        />
                                        <div className="provider-info">
                                            <p className="provider-name">
                                                {campaign.providerName}
                                            </p>
                                            <p className="campaign-speed">
                                                {campaign.speed} Mbps
                                            </p>
                                        </div>
                                    </div>

                                    <div className="card-body">
                                        <h3 className="campaign-title">{campaign.title}</h3>

                                        {renderPriceInfo(campaign)}

                                        <ul className="features-list">
                                            {campaign.features.slice(0, 3).map((feature, index) => (
                                                <li key={index}>
                                                    <svg
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M13.3333 4L5.99996 11.3333L2.66663 8"
                                                            stroke="#33c5ff"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="card-footer">
                                        <button type="button" className="view-details-button">
                                            Detayları Gör
                                            <svg
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <path
                                                    d="M3.33337 8H12.6667M12.6667 8L8.00004 3.33334M12.6667 8L8.00004 12.6667"
                                                    stroke="currentColor"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="no-results">
                            <h3>Seçtiğiniz kriterlere uygun kampanya bulunamadı</h3>
                            <p>Filtrelerinizi genişleterek tekrar deneyebilirsiniz</p>
                            <button className="clear-filters-button" onClick={resetFilters}>
                                Filtreleri Sıfırla
                            </button>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
};

export default CampaignsPage;